import { useState } from 'react';
import { clsx } from 'clsx';
import type { ChildMeta } from '../../types/resource';
import { ChildResourceTable } from './ChildResourceTable';

interface ChildrenPanelProps {
  parentResource: string;
  parentRowId: number | string;
  children: ChildMeta[];
}

/**
 * Renders the inline expansion panel for a parent row. Delegates to
 * one `ChildResourceTable` per declared child resource. Multiple
 * children are surfaced as tabs (child resources are heterogeneous
 * — separate tables read better than a single crowded one). A
 * single child skips the tab strip and renders directly.
 *
 * Sequenced by `child.sequence` when the meta provides it so the
 * tab order stays authored-by-backend rather than dictionary-random.
 */
export function ChildrenPanel({ parentResource, parentRowId, children }: ChildrenPanelProps) {
  const ordered = [...children].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const [activeChild, setActiveChild] = useState<string>(() => ordered[0]?.resource ?? '');

  if (ordered.length === 0) return null;

  if (ordered.length === 1) {
    const only = ordered[0];
    return (
      <div>
        <ChildHeading label={only.display_title || humanise(only.resource)} />
        <ChildResourceTable
          childResource={only.resource}
          parentResource={parentResource}
          parentRowId={parentRowId}
        />
      </div>
    );
  }

  return (
    <div>
      <div role="tablist" className="mb-3 flex flex-wrap gap-1 border-b border-rgf-gray-200">
        {ordered.map((child) => {
          const isActive = activeChild === child.resource;
          return (
            <button
              key={child.resource}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveChild(child.resource)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-t-md border-b-2 px-3 py-2 text-xs font-medium transition',
                isActive
                  ? 'border-rgf-primary-500 text-rgf-primary-700'
                  : 'border-transparent text-rgf-gray-500 hover:text-rgf-gray-900',
              )}
            >
              {child.display_title || humanise(child.resource)}
            </button>
          );
        })}
      </div>
      {ordered.map((child) => (
        <div
          key={child.resource}
          role="tabpanel"
          hidden={activeChild !== child.resource}
        >
          {activeChild === child.resource && (
            <ChildResourceTable
              childResource={child.resource}
              parentResource={parentResource}
              parentRowId={parentRowId}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ChildHeading({ label }: { label: string }) {
  return (
    <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-rgf-gray-500">
      {label}
    </h3>
  );
}

function humanise(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
